import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { API_BASE, onNetworkChange } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { listAll, purgeDone, subscribe, syncOutbox, type OutboxItem, type Owner } from './outbox'

interface OfflineState {
  /** False when the browser is offline or the API cannot be reached. */
  online: boolean
  /** Items created by the signed-in user in this hospital (all statuses). */
  mine: OutboxItem[]
  /** Items on this device that belong to other users (sent when they sign in). */
  othersPending: number
  owner: Owner | null
  syncing: boolean
  syncNow: () => Promise<void>
}

const Ctx = createContext<OfflineState | null>(null)

async function ping(): Promise<boolean> {
  try {
    const ctl = new AbortController()
    const t = setTimeout(() => ctl.abort(), 4000)
    const r = await fetch(`${API_BASE}/public/ping`, { cache: 'no-store', signal: ctl.signal })
    clearTimeout(t)
    return r.ok
  } catch {
    return false
  }
}

export function OfflineProvider({ children }: { children: ReactNode }) {
  const { me, status } = useAuth()
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine)
  const [items, setItems] = useState<OutboxItem[]>([])
  const [syncing, setSyncing] = useState(false)
  const owner = useMemo<Owner | null>(() => (me && status === 'authenticated' ? { tenantId: me.tenant.id, userId: me.id } : null), [me, status])
  const wasOnline = useRef(online)

  const refresh = useCallback(async () => {
    try {
      setItems(await listAll())
    } catch {
      setItems([])
    }
  }, [])

  useEffect(() => {
    void refresh()
    void purgeDone()
    const unsub = subscribe(() => void refresh())
    return () => {
      unsub()
    }
  }, [refresh])

  useEffect(() => {
    const up = () => void ping().then(setOnline)
    const down = () => setOnline(false)
    window.addEventListener('online', up)
    window.addEventListener('offline', down)
    const unsub = onNetworkChange((ok) => setOnline(ok))
    return () => {
      window.removeEventListener('online', up)
      window.removeEventListener('offline', down)
      unsub()
    }
  }, [])

  // While offline, probe the server so we notice recovery even without a browser 'online' event.
  useEffect(() => {
    if (online) return
    const id = setInterval(() => void ping().then((ok) => ok && setOnline(true)), 5000)
    return () => clearInterval(id)
  }, [online])

  const mine = useMemo(() => (owner ? items.filter((i) => i.tenantId === owner.tenantId && i.userId === owner.userId) : []), [items, owner])
  const othersPending = items.filter((i) => i.status !== 'done' && (!owner || i.userId !== owner.userId || i.tenantId !== owner.tenantId)).length
  const pending = mine.filter((i) => i.status === 'pending').length

  const syncNow = useCallback(async () => {
    if (!owner) return
    setSyncing(true)
    try {
      const n = await syncOutbox(owner)
      if (n > 0) toast.success(`${n} offline ${n === 1 ? 'record' : 'records'} sent to the server`, { description: 'Open “Offline records” to see real MRNs and tokens.' })
    } finally {
      setSyncing(false)
    }
  }, [owner])

  // Sync when the connection comes back, on sign-in, and every 30 s while items are pending.
  useEffect(() => {
    if (online && !wasOnline.current) toast.success('Connection restored')
    if (!online && wasOnline.current) toast.warning('Connection lost', { description: 'New registrations and walk-ins will be saved on this device.' })
    wasOnline.current = online
    if (online && owner && pending > 0) void syncNow()
  }, [online, owner, pending, syncNow])

  useEffect(() => {
    if (!online || !owner || pending === 0) return
    const id = setInterval(() => void syncNow(), 30_000)
    return () => clearInterval(id)
  }, [online, owner, pending, syncNow])

  const value = useMemo(() => ({ online, mine, othersPending, owner, syncing, syncNow }), [online, mine, othersPending, owner, syncing, syncNow])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useOffline() {
  const c = useContext(Ctx)
  if (!c) throw new Error('useOffline must be used inside OfflineProvider')
  return c
}
